import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_HOST } from "../../constants/constants";
import { prepareHeaders } from "./bearer";
import { env } from "../../constants/env-config";

// Query string with repeated keys for arrays (operationType=TRADE&operationType=SPLIT); unset values are left out.
function queryString(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    (Array.isArray(value) ? value : [value]).forEach((item) => search.append(key, item));
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

// broker-report-sync decisions: re-read the lists and the account after a success and after 404 / 409 (the state
// changed under the investor); 400 / 5xx / network changed nothing.
const decisionTags = (accountIds) => (result, error) => {
  if (error && error.status !== 404 && error.status !== 409) return [];
  return [
    { type: "SynchronizingInvestingAccounts" },
    { type: "SynchronizingFiles" },
    { type: "SynchronizedFiles" },
    ...accountIds.map((id) => ({ type: "InvestingAccount", id })),
  ];
};

const taxApi = createApi({
  reducerPath: "tax",
  tagTypes: [
    "UnsyncedBrokerReports",
    "SynchronizingFiles",
    "SynchronizedFiles",
    "SynchronizingInvestingAccounts",
    "InvestingAccount",
    "TaxDeclarationsYear",
  ],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_HOST,
    prepareHeaders,
  }),
  endpoints(builder) {
    return {
      // Accounts Pages
      fetchMe: builder.query({
        query: () => {
          return {
            method: "GET",
            url: "/me",
          };
        },
      }),
      createEncryptionKey: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: "/broker-reports/encryption/key",
            body: {
              rsaPublicKey: data.key,
            },
          };
        },
      }),
      sendEncryptedFile: builder.mutation({
        query: (data) => {
          const formData = new FormData();
          formData.append("file", data.file, data.fileName);
          return {
            method: "POST",
            url: `/broker-reports?brokerReportType=${data.type}&encryptionId=${data.id}`,
            body: formData,
          };
        },
      }),
      // Unified broker report upload (SCR-01): no brokerReportType — the server recognizes the
      // format by content (ADR-0010). sendEncryptedFile above stays as is for the existing screen.
      uploadBrokerReport: builder.mutation({
        invalidatesTags: (result) => (result ? [{ type: "UnsyncedBrokerReports" }] : []),
        query: (data) => {
          const formData = new FormData();
          formData.append("file", data.file, data.fileName);
          return {
            method: "POST",
            url: `/broker-reports?encryptionId=${data.encryptionId}`,
            body: formData,
          };
        },
      }),
      listUnsyncedBrokerReports: builder.query({
        providesTags: [{ type: "UnsyncedBrokerReports" }],
        query: (data) => {
          return {
            method: "GET",
            url: "/broker-reports/unsynced",
          };
        },
      }),
      deleteFile: builder.mutation({
        invalidatesTags: (result, error, data) => {
          return [{ type: "SynchronizingFiles" }, { type: "SynchronizingInvestingAccounts" }, { type: "UnsyncedBrokerReports" }];
        },
        query: (data) => {
          return {
            method: "DELETE",
            url: `/broker-reports/${data.id}`,
          };
        },
      }),
      synchronizeFiles: builder.mutation({
        invalidatesTags: (result, error, data) => {
          // A failed synchronize changed nothing — no re-read (F-07).
          if (error) return [];
          return [{ type: "SynchronizingFiles" }, { type: "SynchronizingInvestingAccounts" }, { type: "UnsyncedBrokerReports" }];
        },
        query: (data) => {
          return {
            method: "POST",
            url: "/investing-accounts/synchronize",
            body: {
              brokerReportIds: data.ids,
            },
          };
        },
      }),
      synchronizeFinal: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: "/investing-accounts/synchronize/final",
            body: {
              investingAccounts: data.accounts,
            },
          };
        },
      }),
      synchronizingFiles: builder.query({
        // Always polled live: an unsubscribed (even still pending) entry must not outlive its screen.
        keepUnusedDataFor: 0,
        providesTags: (result, error, { id }) => [
          { type: "SynchronizingFiles", id },
        ],
        query: (data) => {
          return {
            method: "GET",
            url: "/investing-accounts/synchronizing",
            params: {
              dataProvider: data.params.dataProvider,
            },
          };
        },
      }),
      synchronizedFiles: builder.query({
        providesTags: (result, error, { id }) => [
          { type: "SynchronizedFiles", id },
        ],
        query: (data) => {
          return {
            method: "GET",
            url: "/investing-accounts/synchronized",
            params: {
              dataProvider: data.dataProvider,
              page: data.page,
              size: data.size,
            },
          };
        },
      }),
      deleteAccount: builder.mutation({
        invalidatesTags: (result, error, data) => {
          return [
            { type: "SynchronizingFiles" },
            { type: "SynchronizedFiles" },
          ];
        },
        query: (data) => {
          return {
            method: "DELETE",
            url: `/investing-accounts/${data.id}`,
          };
        },
      }),

      // broker-report-sync (contracts/backend/openapi.yaml, revision 2026-10-14)
      readSynchronizingInvestingAccounts: builder.query({
        // SCR-02 polls it live; an unsubscribed entry must not outlive its screen.
        keepUnusedDataFor: 0,
        providesTags: [{ type: "SynchronizingInvestingAccounts" }],
        query: () => ({
          method: "GET",
          url: "/investing-accounts/synchronizing",
        }),
      }),
      readInvestingAccount: builder.query({
        keepUnusedDataFor: 0,
        providesTags: (result, error, { accountId }) => [{ type: "InvestingAccount", id: accountId }],
        query: ({ accountId }) => ({
          method: "GET",
          url: `/investing-accounts/${encodeURIComponent(accountId)}`,
        }),
      }),
      listInvestingAccountOperations: builder.query({
        providesTags: (result, error, { accountId }) => [{ type: "InvestingAccount", id: accountId }],
        query: ({ accountId, syncUnitId, instrument, operationTypes, taxYear, after, before, limit }) => ({
          method: "GET",
          url:
            `/investing-accounts/${encodeURIComponent(accountId)}/operations` +
            queryString({ syncUnitId, instrument, operationType: operationTypes, taxYear, after, before, limit }),
        }),
      }),
      listInvestingAccountBrokerReports: builder.query({
        providesTags: (result, error, { accountId }) => [{ type: "InvestingAccount", id: accountId }],
        query: ({ accountId, syncUnitId }) => ({
          method: "GET",
          url: `/investing-accounts/${encodeURIComponent(accountId)}/broker-reports` + queryString({ syncUnitId }),
        }),
      }),
      finalizeInvestingAccountsSynchronization: builder.mutation({
        invalidatesTags: (result, error, { investingAccounts }) =>
          decisionTags(investingAccounts.map((account) => account.id))(result, error),
        query: ({ investingAccounts }) => ({
          method: "POST",
          url: "/investing-accounts/synchronize/final",
          body: { investingAccounts },
        }),
      }),
      rollbackSynchronization: builder.mutation({
        invalidatesTags: (result, error, { accountId }) => decisionTags([accountId])(result, error),
        query: ({ accountId, syncUnitId }) => ({
          method: "POST",
          url: `/investing-accounts/${encodeURIComponent(accountId)}/rollback`,
          body: { syncUnitId },
        }),
      }),
      removeFailedBrokerReports: builder.mutation({
        invalidatesTags: (result, error, { accountId }) => decisionTags([accountId])(result, error),
        query: ({ accountId, syncUnitId, brokerReportIds }) => ({
          method: "DELETE",
          url:
            `/investing-accounts/${encodeURIComponent(accountId)}/broker-reports` +
            queryString({ syncUnitId, brokerReportIds }),
        }),
      }),

      // tax-planner (contracts/backend/openapi.yaml)
      readTaxPlannerAvailability: builder.query({
        query: () => ({
          method: "GET",
          url: "/tax-planner/availability",
        }),
      }),
      readTaxPlannerSnapshot: builder.query({
        // Every entry is a new request: the snapshot is never served from cache.
        keepUnusedDataFor: 0,
        query: () => ({
          method: "GET",
          url: "/tax-planner/snapshot",
        }),
      }),

      // Tax Reports Pages
      taxDeclarationsPayments: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: `/tax-declarations/${data.id}/payments?useBonuses=${data.bonusChecked}&channel=${data.bank}`,
          };
        },
      }),
      taxDeclarationsCalculatePreliminary: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: `/tax-declarations/calculate-preliminary?year=${data.year}&useBonuses=${data.bonusChecked}`,
          };
        },
      }),
      taxDeclarationsInvestingAccounts: builder.query({
        query: (data) => {
          return {
            method: "GET",
            url: "/tax-declarations/investing-accounts",
            params: {
              year: data.year,
            },
          };
        },
      }),
      taxDeclarationsYear: builder.query({
        providesTags: (result) => result ? [{ type: 'TaxDeclarationsYear', id: result.yearData?.data?.id }] : [],
        async queryFn(data, _queryApi, _extraOptions, fetchWithBQ) {
          const yearData = await fetchWithBQ({
            method: "GET",
            url: `/tax-declarations/${data.year}`,
          });
          let formData = {};
          if (!yearData.error) {
            const taxReportarray = JSON.parse(env.TAX_REPORTS.replace(/'/g, '"'));
            if(taxReportarray.includes("declaration")) {
              formData["declaration"] = await fetchWithBQ({
                method: "GET",
                url: `/tax-declarations/${yearData.data.id}/declaration`,
                responseHandler: (response) => response.blob(),
              });
            } 
            if(taxReportarray.includes("f1")) {
              formData["f1Form"] = await fetchWithBQ({
                method: "GET",
                url: `/tax-declarations/${yearData.data.id}/f1-form`,
                responseHandler: (response) => response.blob(),
              });
            }
            if(taxReportarray.includes("coverLetter")) {
              formData["coverLetter"] = await fetchWithBQ({
                method: "GET",
                url: `/tax-declarations/${yearData.data.id}/cover-letter`,
                responseHandler: (response) => response.blob(),
              });
            }
            if(taxReportarray.includes("details")) {
              formData["excel"] = await fetchWithBQ({
                method: "GET",
                url: `/tax-declarations/${yearData.data.id}/calculations-excel`,
                responseHandler: (response) => response.blob(),
              });
            }
          }

          return { data: { yearData, formData } };
        },
      }),
      updatePrevPeriodTradingLoss: builder.mutation({
        invalidatesTags: (result, error, data) => error ? [] : [{ type: 'TaxDeclarationsYear', id: data.id }],
        query: (data) => {
          return {
            method: "PATCH",
            url: `/tax-declarations/${data.id}/prev-period-trading-loss/update?prevPeriodTradingLoss=${data.prevPeriodTradingLoss}`,
          };
        },
      }),
      referralInfo: builder.query({
        query: (data) => {
          return {
            method: "GET",
            url: "/referral-info",
          };
        },
      }),
      becomeReferred: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: `/become-referred?referralCode=${data.referralCode}`,
          };
        },
      }),
      autoSynchronize: builder.mutation({
        query: (data) => {
          return {
            method: "POST",
            url: "/investing-accounts/autosynchronize",
            body: {
              publicKey: data.publicKey,
              privateKey: data.privateKey,
            },
          };
        },
      }),
    };
  },
});

export const {
  useFetchMeQuery,
  useCreateEncryptionKeyMutation,
  useSendEncryptedFileMutation,
  useUploadBrokerReportMutation,
  useListUnsyncedBrokerReportsQuery,
  useDeleteFileMutation,
  useSynchronizeFilesMutation,
  useSynchronizingFilesQuery,
  useDeleteAccountMutation,
  useSynchronizeFinalMutation,
  useSynchronizedFilesQuery,
  useReadSynchronizingInvestingAccountsQuery,
  useReadInvestingAccountQuery,
  useListInvestingAccountOperationsQuery,
  useListInvestingAccountBrokerReportsQuery,
  useFinalizeInvestingAccountsSynchronizationMutation,
  useRollbackSynchronizationMutation,
  useRemoveFailedBrokerReportsMutation,
  useReadTaxPlannerAvailabilityQuery,
  useReadTaxPlannerSnapshotQuery,
  useLazyReadTaxPlannerSnapshotQuery,

  useTaxDeclarationsPaymentsMutation,
  useTaxDeclarationsCalculatePreliminaryMutation,
  useTaxDeclarationsYearQuery,
  useTaxDeclarationsInvestingAccountsQuery,
  useLazyTaxDeclarationsInvestingAccountsQuery,
  useUpdatePrevPeriodTradingLossMutation,

  useReferralInfoQuery,
  useBecomeReferredMutation,
  useAutoSynchronizeMutation,
} = taxApi;
export { taxApi };

